import { Avatar, Paper, Text } from "@mantine/core";
import {
  IconCurrencyDollar,
  IconEye,
  IconFile,
  IconMailbox,
  IconMailboxOff,
  IconUser,
} from "@tabler/icons-react";
import { useClientNavigation } from "@utils/hooks/useClientNavigation.ts";
import { useAuthStore } from "@utils/stores/authStore.ts";

const EventTypes: { [key: number]: JSX.Element } = {
  1: <IconUser />,
  2: <IconFile />,
  3: <IconCurrencyDollar />,
  4: <IconMailboxOff />,
};

/**
 * Renders notification cards based on the provided notifications, with the ability to mark notifications as read.
 *
 * @param {any} notifications - The list of notifications to be rendered.
 * @param {function} handleRead - The function to handle marking a notification as read.
 * @param {boolean} newNotification - Indicates whether a new notification is present.
 * @return {JSX.Element} The rendered notification cards.
 */

const NotificationCards = ({
  notifications,
  handleRead,
  newNotification,
}: any = {}) => {
  const { navigateToClient } = useClientNavigation();
  const { role } = useAuthStore();

  const handleEyeClick = (event: React.MouseEvent, notificationId: string) => {
    event.stopPropagation(); // Prevent the click from bubbling up to the Paper component
    handleRead(notificationId);
  };

  const handleCardClick = async (notification: any) => {
    // Only navigate to client profile if the user is not a client
    if (notification?.senderId && role !== "client") {
      // First mark as read if it's a new notification
      if (newNotification) {
        await handleRead(notification.id);
      }
      // Then navigate to the profile
      navigateToClient({ userId: notification.senderId });
    } else if (newNotification) {
      // If user is a client, just mark as read without navigation
      await handleRead(notification.id);
    }
  };

  return (
    <div>
      {notifications?.content?.map(
        (notification: any) =>
          notification?.eventType >= 0 && (
            <div className="notification-items" key={notification.id}>
              <Paper
                className="notification-paper"
                withBorder
                shadow="md"
                onClick={() => handleCardClick(notification)}
                p="md"
                style={{ cursor: "pointer" }}
              >
                <div className="notification-content">
                  <div className="notification-details">
                    <Avatar color="#fff" className="notification-icon">
                      {notification?.eventType <= 4 ? (
                        EventTypes[notification?.eventType + 1]
                      ) : (
                        <IconMailbox />
                      )}
                    </Avatar>
                    <div>
                      <Text>{notification.content}</Text>
                      <Text c={"dimmed"} size="xs">
                        {new Date(
                          notification.dateCreated
                        ).toLocaleDateString()}
                      </Text>
                    </div>
                  </div>
                  {newNotification ? (
                    <button
                      className="notification-button"
                      onClick={(e) => handleEyeClick(e, notification?.id)}
                    >
                      <IconEye size={20} stroke={1.5} />
                    </button>
                  ) : null}
                </div>
              </Paper>
            </div>
          )
      )}
    </div>
  );
};

export default NotificationCards;
